import React, {
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import {
  addOverlay,
  removeOverlay,
  updateOverlay,
} from '../../Actions/Overlay.action';
import Icon from '../Icon';
import clsx from 'clsx';

interface SideMenuProps {
  id: string;
  duration?: string;
  from?: 'left' | 'right' | 'bottom';
  className?: string;
  contentClassName?: string;
  open?: boolean;
  onClosed: () => void;
  showCloseButton?: boolean;
  children?: React.ReactNode;
  title?: string;
  disableClosing?: boolean;
  closeButton?: any;
  components?: {
    Header?: React.FC;
    Footer?: React.FC;
    Content?: React.FC;
  };
  zIndexClass?: string;
}

const SideMenu = forwardRef<HTMLDivElement, SideMenuProps>(
  (
    {
      id,
      duration = '0.2s',
      from = 'right',
      className,
      contentClassName,
      open = false,
      onClosed,
      showCloseButton = true,
      children,
      title,
      disableClosing = false,
      closeButton = null,
      components = {},
      zIndexClass,
    }: SideMenuProps,
    ref
  ) => {
    const [ready, setReady] = useState<boolean>(false);
    const [slideIn, setSlideIn] = useState<boolean>(open);
    const [renderSideMenu, setRenderSideMenu] = useState<boolean>(open);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
    const dispatch = useDispatch();

    if (!id || id == '') {
      throw new Error(
        'A unique side-menu "id" must be passed to side menu component'
      );
    }

    useEffect(() => {
      setReady(true);
    }, []);

    const close = useCallback(() => {
      if (!disableClosing) {
        // Just sets "slideIn" to false, which triggers the animation to close it
        setSlideIn(false);
        return true;
      }

      // False disables overlay click
      return false;
    }, [disableClosing]);

    useEffect(() => {
      dispatch(updateOverlay(id, close));
    }, [dispatch, close, id]);

    useEffect(() => {
      if (!ready) return;
      if (!slideIn && ready) {
        // If not slide in, slide out :^)
        if (timeoutId) {
          // Clear previous timeout to avoid animation delay bug
          clearTimeout(timeoutId);
        }

        var newTimeoutId = setTimeout(() => {
          setTimeoutId(undefined);
          onClosed && onClosed();
          setRenderSideMenu(false);
        }, parseFloat(duration) * 1000);

        setTimeoutId(newTimeoutId);
        dispatch(removeOverlay(id));
      }
    }, [slideIn]);

    useEffect(() => {
      if (!ready) return;

      // Triggered every time "open" is changed, initially as well
      // Timeout to not trigger on the same frame as the render, to enable the slide-in
      setTimeout(() => setSlideIn(open), 1);

      if (open) {
        setRenderSideMenu(true);
        dispatch(addOverlay(id, close, zIndexClass));
      }
    }, [open, close]);

    const CloseIcon = () => (
      <button onClick={close} className="absolute right-2 top-2 p-2">
        <Icon
          className=" h-5 w-5 cursor-pointer md:h-6 md:w-6"
          name="close"
        />
      </button>
    );

    const slideInClasses = useMemo(() => {
      switch (from) {
        case 'bottom':
          return slideIn ? 'bottom-0' : '-bottom-full';
        case 'left':
          return slideIn ? 'left-2' : '-left-full';
        case 'right':
        default:
          return slideIn ? 'right-2' : '-right-full';
      }
    }, [slideIn, from]);

    return renderSideMenu
      ? ReactDOM.createPortal(
          <div
            className={clsx(
              className,
              'fixed z-50 flex h-full max-h-[calc(100vh-1rem)] flex-col overflow-y-auto rounded bg-white transition-all duration-[400ms] ease-in-out',
              from == 'bottom'
                ? 'max-h-[80%] w-full'
                : 'top-2 w-[calc(100vw-1rem)] max-w-[512px] store-screen:max-h-[1080px]',
              slideIn ? 'open' : 'closing',
              slideInClasses
            )}
            ref={ref}
            style={{ transitionDuration: duration }}
          >
            <div className="relative flex items-center p-4 capitalize">
              {components.Header && (
                <>
                  {showCloseButton &&
                    !disableClosing &&
                    (!closeButton ? (
                      <CloseIcon />
                    ) : (
                      cloneElement(closeButton, {
                        onClick: close,
                      })
                    ))}
                  <components.Header />
                </>
              )}
              {!components.Header &&
                showCloseButton &&
                !disableClosing &&
                (!closeButton ? (
                  <CloseIcon />
                ) : (
                  cloneElement(closeButton, {
                    onClick: close,
                  })
                ))}
              {title && <h2 className="pr-6">{title}</h2>}
            </div>
            {components?.Content && (
              <components.Content>{children}</components.Content>
            )}
            {!components?.Content && (
              <div className={clsx(contentClassName ?? 'gap-4 pl-4 pr-4')}>
                {children}
              </div>
            )}
            {components.Footer != null && (
              <div className="flex justify-center bg-gray-50 p-2">
                <components.Footer />
              </div>
            )}
          </div>,
          document.body
        )
      : null;
  }
);

SideMenu.displayName = 'SideMenu';

export default SideMenu;
